import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { range } from 'lodash';

import { useButton, useDeviceInfo, useI18n, useSelector } from '~Hooks';
import * as actions from '~actions';
import { handyBreakpoint } from '~Styles/breakpoints';
import logo from '~Assets/Logos/baemingo_logo_white.svg';
import { GreenButton } from '~Styles';
import { checkBrowserAllowClick } from '~utilities';
import { useStorage } from '~Components/Storage';

const Background = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 1000;
  background: rgb(27 35 44 / 90%);
`;

const ContentWrapper = styled.div<{ isPhone: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background: var(--colorBlack);
  padding-top: ${({ isPhone }) => (isPhone ? '64px' : '0')};
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 20%;
  ${handyBreakpoint} {
    height: 6%;
    background: var(--colorBlack);
    align-items: flex-end;
  }
`;

const Logo = styled.div`
  width: 55%;
  height: 40%;
  mask-image: url(${logo});
  mask-size: contain;
  background-color: #fff;
  mask-repeat: no-repeat;
  ${handyBreakpoint} {
    width: 216px;
    height: 20px;
  }
`;

const NumpadWrapper = styled.div`
  box-sizing: border-box;
  width: 390px;
  padding: 8px;
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: var(--colorBlack);
  ${handyBreakpoint} {
    width: 100%;
    padding-top: 0;
  }
`;

const Display = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 115px;
  font-size: 64px;
  box-sizing: border-box;
  color: #ffffff;
  overflow: hidden;
  ${handyBreakpoint} {
    height: 98px;
  }
`;

// Can also act as an error message
const Placeholder = styled.span`
  color: var(--colorBlueGray500);
  letter-spacing: 1px;
  width: 100%;
  font-size: 16px;
  text-align: center;
  font-weight: bold;
  text-transform: uppercase;
`;

const ErrorMessage = styled(Placeholder)`
  color: var(--colorStatusRed);
`;

const Numpad = styled.div`
  display: grid;
  font-size: 70px;
  background-color: var(--colorBlack);
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  & > div:not(:nth-child(3n)) {
    margin-right: 8px;
  }
`;

const Digit = styled.div`
  background: var(--colorBlueGray1200);
  box-sizing: border-box;
  margin-bottom: 8px;
  text-align: center;
  color: #eee;
  border-radius: 4px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.64);
  padding: 18px 8px 22px;
  font-size: 32px;
  font-weight: normal;
  height: 80px;
  letter-spacing: 0px;
  line-height: 40px;

  &:active {
    background-image: linear-gradient(
      180deg,
      var(--colorBlueGray1000) 1%,
      var(--colorBlueGray800) 100%
    );
  }
`;

type LoginModalProps = {
  onClose?: () => void;
};

const LoginModal = ({ onClose }: LoginModalProps) => {
  const dispatch = useDispatch();
  const [digits, setDigits] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { i18n } = useI18n();
  const deviceInfo = useDeviceInfo();
  const [storage] = useStorage();
  const allowBrowserClick = checkBrowserAllowClick(storage.allowBrowserClick);

  const companyStaff = useSelector((state) => state.companyStaff);
  const staffs = companyStaff?.staffs ?? [];

  function addDigit(n: number) {
    const newValue = digits + n;

    if (newValue.length > 5) {
      return;
    }

    setDigits(newValue);
  }

  function login(id: string, code: string, name: string) {
    dispatch(actions.staffLogin(id, code, name));
    if (onClose) {
      onClose();
    }
  }

  function failLogin() {
    setErrorMessage(i18n('General.IncorrectStaffcode'));
    setDigits('');
    setTimeout(() => {
      setErrorMessage('');
    }, 4000);
  }

  const handleSubmit = useCallback(() => {
    const staff = staffs.find((s) => s.code === digits);
    if (staff) {
      login(staff.id, digits, staff.name);
    } else {
      failLogin();
    }
  }, [digits, staffs]);

  const submitButton = useButton({
    onClick: handleSubmit,
  });

  if (!staffs.length) {
    return null;
  }

  return (
    <Background
      onTouchStart={() => {
        if (onClose) {
          onClose();
        }
      }}>
      <ContentWrapper isPhone={Boolean(deviceInfo?.isPhone)}>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        <NumpadWrapper>
          <Display>
            {digits.length ? (
              range(digits.length).map(() => '•')
            ) : errorMessage ? (
              <ErrorMessage>{errorMessage}</ErrorMessage>
            ) : (
              <Placeholder>{i18n('Staff.Login.Placeholder')}</Placeholder>
            )}
          </Display>
          <Numpad>
            {range(1, 10).map((n) => (
              <Digit
                key={n}
                onTouchStart={() => addDigit(n)}
                onClick={(e) => {
                  if (!allowBrowserClick) {
                    return;
                  }
                  e.stopPropagation();
                  addDigit(n);
                }}>
                {n}
              </Digit>
            ))}
            <Digit />
            <Digit
              onTouchStart={() => addDigit(0)}
              onClick={(e) => {
                if (!allowBrowserClick) {
                  return;
                }
                e.stopPropagation();
                addDigit(0);
              }}>
              0
            </Digit>
            <Digit
              onTouchStart={() => setDigits('')}
              onClick={(e) => {
                if (!allowBrowserClick) {
                  return;
                }
                e.stopPropagation();
                setDigits('');
              }}>
              C
            </Digit>
          </Numpad>
          <GreenButton {...submitButton} fluid size="xl">
            {i18n('General.Ok')}
          </GreenButton>
        </NumpadWrapper>
      </ContentWrapper>
    </Background>
  );
};

export default LoginModal;
